// Single-input chip: name, units, mode toggle (range vs fixed), numeric
// bounds or fixed value, and a delete button. Owned by CampaignConfigurator
// — receives a controlled AxisSpec and onChange / onRemove callbacks.

import {
  Badge,
  Box,
  Flex,
  IconButton,
  Select,
  Text,
  TextField,
} from '@radix-ui/themes';
import { AxisSpec } from '../types';

type Props = {
  axis: AxisSpec;
  onChange: (next: AxisSpec) => void;
  onRemove?: () => void;
  locked?: boolean;
  error?: string;
};

export default function AxisChip({
  axis,
  onChange,
  onRemove,
  locked,
  error,
}: Props) {
  const setMode = (mode: 'range' | 'fixed') => {
    onChange({ ...axis, mode });
  };
  const setNum = (key: 'min' | 'max' | 'fixedValue', raw: string) => {
    const n = raw === '' ? undefined : Number(raw);
    onChange({ ...axis, [key]: Number.isFinite(n) ? n : undefined });
  };

  return (
    <Box
      style={{
        border: error
          ? '1px solid var(--red-a7)'
          : '1px solid var(--gray-a5)',
        borderRadius: 8,
        padding: '10px 12px',
        background: 'var(--gray-a2)',
        minWidth: 260,
      }}
    >
      <Flex gap="2" align="center" wrap="wrap">
        <Text weight="medium" style={{ minWidth: 60 }}>
          {axis.name}
        </Text>
        <Badge color="gray" variant="soft">
          {axis.units}
        </Badge>

        <Select.Root
          value={axis.mode}
          onValueChange={(v) => setMode(v as 'range' | 'fixed')}
          disabled={locked}
          size="1"
        >
          <Select.Trigger />
          <Select.Content>
            <Select.Item value="range">range</Select.Item>
            <Select.Item value="fixed">fixed</Select.Item>
          </Select.Content>
        </Select.Root>

        {axis.mode === 'range' ? (
          <Flex gap="1" align="center">
            <TextField.Root
              size="1"
              type="number"
              placeholder="min"
              value={axis.min ?? ''}
              onChange={(e) => setNum('min', e.currentTarget.value)}
              disabled={locked}
              style={{ width: 80 }}
            />
            <Text color="gray">to</Text>
            <TextField.Root
              size="1"
              type="number"
              placeholder="max"
              value={axis.max ?? ''}
              onChange={(e) => setNum('max', e.currentTarget.value)}
              disabled={locked}
              style={{ width: 80 }}
            />
          </Flex>
        ) : (
          <TextField.Root
            size="1"
            type="number"
            placeholder="value"
            value={axis.fixedValue ?? ''}
            onChange={(e) => setNum('fixedValue', e.currentTarget.value)}
            disabled={locked}
            style={{ width: 100 }}
          />
        )}

        {onRemove && !locked && (
          <IconButton
            size="1"
            variant="ghost"
            color="gray"
            onClick={onRemove}
            aria-label={`Remove ${axis.name}`}
          >
            x
          </IconButton>
        )}
      </Flex>

      {error && (
        <Text size="1" color="red" style={{ marginTop: 4 }}>
          {error}
        </Text>
      )}
    </Box>
  );
}
